// src/pages/OpenShift.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  Avatar,
  Chip,
  Card,
  CardContent,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Groups,
  Schedule,
  Person
} from '@mui/icons-material';

import { getData, saveSessionItem } from '../services/storageService';

export default function OpenShift() {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [turno, setTurno] = useState('mañana');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await getData('users') || [];
        const operatorsAndTechnicians = users.filter(user => 
          user.role === 'operator' || user.role === 'technician'
        );
        setAllUsers(operatorsAndTechnicians);
      } catch (error) {
        console.error("Failed to load users for shift selection:", error);
        setError('Error al cargar la lista de usuarios');
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  const handleOperatorChange = (username) => {
    setSelectedOperators(prev =>
      prev.includes(username)
        ? prev.filter(op => op !== username)
        : [...prev, username]
    );
  };

  const handleStartShift = async () => {
    if (selectedOperators.length === 0) {
      setError('Por favor, selecciona al menos un operador.');
      return;
    }

    setLoading(true);
    setError('');

    const newRecordId = Date.now();
    const newShiftData = {
      id: newRecordId,
      fecha: new Date().toISOString().slice(0, 10),
      turno: turno,
      operators: selectedOperators,
      createdAt: new Date().toISOString(),
      tunel: {}, 
      rech_tunel: {}, 
      lav_ciclos: {}, 
      lav_rechazos: {}, 
      cont: {}, 
      pesoCliente: []
    };

    try {
      await saveSessionItem('current_shift', newShiftData);
      
      // Анімація успішного старту
      setTimeout(() => {
        navigate('/form');
      }, 800);
    } catch (error) {
      console.error("Failed to start shift:", error);
      setError("Error al guardar los datos del turno.");
      setLoading(false);
    }
  };

  const getShiftInfo = (shift) => {
    switch (shift) {
      case 'mañana':
        return { 
          title: 'Turno Mañana', 
          time: '06:00 – 14:00',
          color: 'primary' 
        };
      case 'tarde':
        return { 
          title: 'Turno Tarde', 
          time: '14:00 – 22:00',
          color: 'secondary' 
        };
      default:
        return { 
          title: 'Turno', 
          time: '',
          color: 'default' 
        };
    }
  };

  const shiftInfo = getShiftInfo(turno);

  if (usersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light} 100%)`,
        p: 3
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Заголовок */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: theme.palette.primary.main,
              mx: 'auto',
              mb: 2
            }}
          >
            <Groups sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            Abrir Nuevo Turno
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Configura los parámetros del turno de trabajo
          </Typography>
        </Box>

        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box component="form" onSubmit={e => { e.preventDefault(); handleStartShift(); }}>
            {/* Вибір зміни */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Selecciona el Turno
                  </Typography>
                </Box>
                
                <FormControl fullWidth>
                  <InputLabel>Turno de Trabajo</InputLabel>
                  <Select
                    value={turno}
                    label="Turno de Trabajo"
                    onChange={(e) => setTurno(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="mañana">Turno Mañana (06:00 – 14:00)</MenuItem>
                    <MenuItem value="tarde">Turno Tarde (14:00 – 22:00)</MenuItem>
                  </Select>
                </FormControl>

                {turno && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.primary.light, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
                      {shiftInfo.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Horario: {shiftInfo.time}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Вибір операторів */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Selecciona los Operadores
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Elige los operadores y técnicos que trabajarán en este turno
                </Typography>

                {allUsers.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No hay operadores disponibles. Primero crea usuarios en el sistema.
                  </Alert>
                ) : (
                  <>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        maxHeight: 300, 
                        overflow: 'auto', 
                        p: 2, 
                        borderRadius: 2,
                        mb: 2
                      }}
                    >
                      <FormGroup>
                        {allUsers.map(user => (
                          <FormControlLabel
                            key={user.username}
                            control={
                              <Checkbox
                                checked={selectedOperators.includes(user.username)}
                                onChange={() => handleOperatorChange(user.username)}
                                color="primary"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip 
                                  label={user.role} 
                                  size="small" 
                                  sx={{ mr: 1 }}
                                  color={user.role === 'technician' ? 'secondary' : 'primary'}
                                />
                                {user.username}
                              </Box>
                            }
                          />
                        ))}
                      </FormGroup>
                    </Paper>

                    {/* Вибрано операторів */}
                    {selectedOperators.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Operadores seleccionados ({selectedOperators.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedOperators.map(op => (
                            <Chip 
                              key={op} 
                              label={op} 
                              size="small"
                              onDelete={() => handleOperatorChange(op)}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}

            {/* Кнопка старту */}
            <Button
              type="submit"
              variant="contained"
              color="success"
              size="large"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              sx={{
                py: 2,
                borderRadius: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
              disabled={loading || allUsers.length === 0}
            >
              {loading ? 'Iniciando Turno...' : `Iniciar ${shiftInfo.title}`}
            </Button>

            {/* Інформація */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Fecha: {new Date().toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}


